import React from 'react';
import { usePageContext } from 'vike-react/usePageContext';

import { Link } from '../../../components/Link';
import { CompanyLogo } from '../../../components/CompanyLogo';
import type { AlgoliaCompanyRecord } from '../../../types/algolia-company-record';
import { getCombinedRemoteHybridPercentForDisplay } from '../../../utils/data/get-combined-remote-hybrid-percent-for-display';
import { buildBestCompaniesListingRatingExplainer } from '../../../utils/best-companies/build-best-companies-listing-rating-explainer';
import { normalizeBestCompaniesLocale } from '../../../utils/seo/best-companies-meta';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';

function ScoreBadge({ score }: { score?: number }) {
  if (score == null) return null;
  const tier =
    score >= 70
      ? 'bg-jc-cta text-white border border-jc-filter-job-border'
      : score >= 50
        ? 'bg-jc-gold-bg text-jc-gold border border-jc-gold-line/35'
        : 'bg-jc-filter-company-bg text-jc-filter-company-text border border-jc-filter-company-border';
  return (
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-xs font-bold sm:h-10 sm:w-10 sm:rounded-lg sm:text-sm ${tier}`}
      title={`${score}`}
    >
      {score}
    </div>
  );
}

function TrendPill({ trend }: { trend?: string }) {
  if (!trend) return null;
  const config = {
    growing: {
      label: 'Growing',
      cls: 'bg-jc-filter-job-bg text-jc-filter-job-text border border-jc-filter-job-border/60',
    },
    declining: {
      label: 'Declining',
      cls: 'bg-jc-filter-company-bg text-jc-filter-company-text border border-jc-filter-company-border/70',
    },
    stable: {
      label: 'Stable',
      cls: 'bg-jc-elevated text-jc-on-elevated border border-jc-border',
    },
  }[trend as 'growing' | 'declining' | 'stable'] ?? {
    label: trend,
    cls: 'bg-jc-elevated text-jc-muted border border-jc-border',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.cls}`}>{config.label}</span>
  );
}

export function BestCompaniesCompanyRow({ company, rank }: { company: AlgoliaCompanyRecord; rank: number }) {
  const pageContext = usePageContext();
  const locale = normalizeBestCompaniesLocale((pageContext?.locale as string | undefined) ?? 'en');
  const topRolesText = company.topRoles?.slice(0, 3).join(' · ');
  const combinedRemoteHybridPct = getCombinedRemoteHybridPercentForDisplay(company);
  const dist = company.remotenessDistribution;
  const flexText =
    combinedRemoteHybridPct != null
      ? dist != null && dist.hybrid === 0 && dist.remote > 0
        ? `${combinedRemoteHybridPct}% remote`
        : `${combinedRemoteHybridPct}% remote/hybrid`
      : null;

  const salaryText =
    company.medianSalaryMin && company.medianSalaryMax
      ? `€${Math.round(company.medianSalaryMin / 100) * 100}–€${Math.round(company.medianSalaryMax / 100) * 100}/mo`
      : null;

  const ratingExplainer = buildBestCompaniesListingRatingExplainer(company, locale);

  return (
    <Link
      href={companyProfileUrlPath(locale, company.objectID)}
      locale={locale as 'en' | 'fi' | 'sv'}
      className="flex items-start gap-2.5 rounded-xl border border-jc-border bg-jc-surface p-3 shadow-md transition-all hover:border-jc-accent-soft hover:bg-jc-elevated/40 group sm:gap-4 sm:rounded-2xl sm:p-4"
    >
      <div className="w-5 shrink-0 pt-1 text-center text-xs font-bold text-jc-muted sm:w-6 sm:pt-2 sm:text-sm">
        #{rank}
      </div>

      <div className="shrink-0">
        <CompanyLogo
          companyName={company.objectID}
          size={48}
          alt={company.companyName ?? ''}
          variant="default"
          isTyomarkkinatori={false}
          loading={rank <= 5 ? 'eager' : 'lazy'}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="mb-0.5 flex items-start justify-between gap-1.5 sm:mb-1 sm:gap-2">
          <h3 className="truncate text-sm font-semibold text-jc-ink transition-colors group-hover:text-jc-accent">
            {company.companyName}
          </h3>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <TrendPill trend={company.hiringTrend} />
            <ScoreBadge score={company.employerScore} />
          </div>
        </div>
        <p className="mb-1 text-xs text-jc-muted sm:mb-1.5">
          {[company.industryEn, company.registryCity].filter(Boolean).join(' · ')}
        </p>
        <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-jc-muted sm:gap-x-3 sm:text-xs">
          {flexText && <span className="text-jc-accent font-medium">{flexText}</span>}
          {salaryText && <span>Median {salaryText}</span>}
          {topRolesText && <span className="text-jc-contrast-subtle">{topRolesText}</span>}
        </div>
        <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-jc-muted sm:mt-1.5 sm:text-xs sm:leading-relaxed">
          {ratingExplainer}
        </p>
      </div>
    </Link>
  );
}
